<!--
  - SPDX-FileCopyrightText: 2021 Nextcloud GmbH and Nextcloud contributors
  - SPDX-License-Identifier: AGPL-3.0-or-later
-->

<template>
	<div class="resource-list-item">
		<AvatarParticipationStatus
			:attendeeIsOrganizer="false"
			:isViewedByOrganizer="isViewedByOrganizer"
			:isResource="true"
			:isSuggestion="isSuggestion"
			:participationStatus="participationStatus"
			:scheduleStatus="scheduleStatus"
			:availability="availability"
			:organizerDisplayName="organizerDisplayName"
			:commonName="commonName" />
		<div class="resource-list-item__displayname">
			{{ commonName }}
		</div>
		<div class="resource-list-item__actions">
			<Actions v-if="isViewedByOrganizer && isSuggestion">
				<ActionButton @click="addSuggestion">
					<template #icon>
						<Plus :size="20" decorative />
					</template>
					{{ $t('calendar', 'Add resource') }}
				</ActionButton>
			</actions>
			<Actions v-else-if="isViewedByOrganizer">
				<ActionCaption
					v-if="seatingCapacity"
					:name="seatingCapacity" />
				<ActionCaption
					v-if="roomType"
					:name="roomType" />
				<ActionCaption
					v-if="hasProjector"
					:name="$t('calendar', 'Has a projector')" />
				<ActionCaption
					v-if="hasWhiteboard"
					:name="$t('calendar', 'Has a whiteboard')" />
				<ActionCaption
					v-if="isAccessible"
					:name="$t('calendar', 'Wheelchair accessible')" />
				<ActionSeparator v-if="seatingCapacity || roomType || hasProjector || hasWhiteboard || isAccessible" />
				<ActionButton
					v-if="principal"
					:closeAfterClick="true"
					@click="showAvailabilityModal = true">
					<template #icon>
						<CalendarSearch :size="20" decorative />
					</template>
					{{ $t('calendar', 'Check availability') }}
				</ActionButton>
				<ActionButton @click="removeResource">
					<template #icon>
						<Delete :size="20" decorative />
					</template>
					{{ $t('calendar', 'Remove resource') }}
				</ActionButton>
			</actions>
		</div>
		<RoomAvailabilityModal
			v-if="showAvailabilityModal"
			:show="showAvailabilityModal"
			:startDate="calendarObjectInstance.startDate"
			:endDate="calendarObjectInstance.endDate"
			:rooms="[roomPrincipal]"
			:organizer="currentUserPrincipalAsAttendee"
			@update:show="(value) => { showAvailabilityModal = value }" />
	</div>
</template>

<script>
import {
	NcActionButton as ActionButton,
	NcActionCaption as ActionCaption,
	NcActions as Actions,
	NcActionSeparator as ActionSeparator,
} from '@nextcloud/vue'
import { mapStores } from 'pinia'
import CalendarSearch from 'vue-material-design-icons/CalendarSearch.vue'
import Plus from 'vue-material-design-icons/Plus.vue'
import Delete from 'vue-material-design-icons/TrashCanOutline.vue'
import AvatarParticipationStatus from '@/components/Editor/AvatarParticipationStatus.vue'
import RoomAvailabilityModal from '@/components/Editor/FreeBusy/RoomAvailabilityModal.vue'
import { mapPrincipalObjectToAttendeeObject } from '@/models/attendee.js'
import { formatRoomType } from '@/models/resourceProps'
import { principalPropertySearchByDisplaynameOrEmail } from '@/services/caldavService.js'
import useCalendarObjectInstanceStore from '@/store/calendarObjectInstance.js'
import usePrincipalsStore from '@/store/principals.js'
import { removeMailtoPrefix } from '@/utils/attendee.js'
import logger from '@/utils/logger.js'

export default {
	name: 'ResourceListItem',
	components: {
		AvatarParticipationStatus,
		ActionButton,
		ActionCaption,
		ActionSeparator,
		Actions,
		CalendarSearch,
		Delete,
		Plus,
		RoomAvailabilityModal,
	},

	props: {
		resource: {
			type: Object,
			required: true,
		},

		organizerDisplayName: {
			type: String,
			required: true,
		},

		isSuggestion: {
			type: Boolean,
			default: false,
		},

		isViewedByOrganizer: {
			type: Boolean,
			default: false,
		},

		availability: {
			type: String, // 'checking' | 'available' | 'unavailable' | null
			default: null,
		},
	},

	emits: ['addSuggestion', 'removeResource'],

	data() {
		return {
			principal: null,
			showAvailabilityModal: false,
		}
	},

	computed: {
		...mapStores(usePrincipalsStore, useCalendarObjectInstanceStore),
		calendarObjectInstance() {
			return this.calendarObjectInstanceStore.calendarObjectInstance
		},

		/**
		 * The room principal in the shape expected by the availability modal.
		 * The principal property search returns raw cdav principals where the
		 * email address is stored in email rather than emailAddress.
		 *
		 * @return {?object}
		 */
		roomPrincipal() {
			if (!this.principal) {
				return null
			}

			return {
				displayname: this.principal.displayname,
				emailAddress: this.principal.email,
				calendarUserType: this.principal.calendarUserType,
			}
		},

		/**
		 * Return the current user principal as an ORGANIZER attendee object
		 *
		 * @return {object}
		 */
		currentUserPrincipalAsAttendee() {
			return mapPrincipalObjectToAttendeeObject(
				this.principalsStore.getCurrentUserPrincipal,
				true,
			)
		},

		commonName() {
			if (this.resource.commonName) {
				return this.resource.commonName
			}

			if (this.resource.uri) {
				return removeMailtoPrefix(this.resource.uri)
			}

			return this.resource.uri
		},

		isAccessible() {
			return this.hasFeature('WHEELCHAIR-ACCESSIBLE')
		},

		hasProjector() {
			return this.hasFeature('PROJECTOR')
		},

		hasWhiteboard() {
			return this.hasFeature('WHITEBOARD')
		},

		seatingCapacity() {
			const seatingCapacity = this.principal?.roomSeatingCapacity
			if (!seatingCapacity) {
				return null
			}

			return this.$n(
				'calendar',
				'{seatingCapacity} seat',
				'{seatingCapacity} seats',
				seatingCapacity,
				{
					seatingCapacity,
				},
			)
		},

		roomType() {
			const roomType = this.principal?.roomType
			return formatRoomType(roomType) ?? roomType
		},

		participationStatus() {
			if (this.isSuggestion) {
				return ''
			}

			return this.resource.participationStatus
		},

		scheduleStatus() {
			return this.resource.attendeeProperty?.getParameterFirstValue('SCHEDULE-STATUS') ?? ''
		},
	},

	watch: {
		async resource() {
			await this.fetchPrincipal()
		},
	},

	async mounted() {
		await this.fetchPrincipal()
	},

	methods: {
		/**
		 * Add this suggestions to the event
		 */
		addSuggestion() {
			this.$emit('addSuggestion', this.resource)
		},

		/**
		 * Removes a resource from the event
		 */
		removeResource() {
			this.$emit('removeResource', this.resource)
		},

		/**
		 * Check if this resource has a feature
		 *
		 * @param {string} feature The name of the feature
		 * @return {boolean} True if this resource has the given feature
		 */
		hasFeature(feature) {
			const features = this.principal?.roomFeatures?.split(',') ?? []
			return !!features.find((featureToCheck) => featureToCheck === feature)
		},

		/**
		 * Try to fetch the principal belonging to this resource
		 */
		async fetchPrincipal() {
			const uri = removeMailtoPrefix(this.resource.uri)
			let principals = await principalPropertySearchByDisplaynameOrEmail(uri)
			principals = principals.filter((principal) => removeMailtoPrefix(principal.email) === uri)
			if (principals.length === 0) {
				logger.debug('No principal for resource found', {
					uri: this.resource.uri,
				})
				this.principal = null
				return
			} else if (principals.length > 1) {
				logger.debug('More than one principal for resource found', {
					uri: this.resource.uri,
					principals,
				})
			}

			this.principal = principals[0]
		},
	},
}
</script>

<style lang="scss" scoped>
.resource-list-item__displayname {
	margin-bottom: 17px;
	text-overflow: ellipsis;
	overflow: hidden;
	white-space: nowrap;
}

.avatar-participation-status {
	margin-top: 5px;
}
</style>
